import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { trackEcommerce, trackButtonClick } from '../../../hooks/useGoogleAnalytics';

const ProductCard = ({ product, onAddToCart, onQuickView }) => {
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.[0] || null);
  const [quantity, setQuantity] = useState(1);
  const [isHovered, setIsHovered] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);

  const handleWishlistToggle = () => {
    setIsInWishlist(!isInWishlist);
    // Track wishlist action
    trackButtonClick(isInWishlist ? 'remove_from_wishlist' : 'add_to_wishlist', 'ecommerce');
  };

  const handleAddToCart = () => {
    onAddToCart?.(product);
    
    // Track e-commerce event
    trackEcommerce('add_to_cart', {
      item_id: product?.id?.toString() || 'unknown',
      item_name: product?.name || 'Unknown Product',
      category: product?.category || 'cleaning-products',
      price: product?.price || 0,
      currency: 'USD'
    });
    
    trackButtonClick('add_to_cart', 'ecommerce');
  };

  const handleQuickView = () => {
    onQuickView?.(product);
    trackEcommerce('view_item', {
      item_id: product?.id?.toString() || 'unknown',
      item_name: product?.name || 'Unknown Product',
      category: product?.category || 'cleaning-products',
      price: product?.price || 0,
      currency: 'USD'
    });
    
    trackButtonClick('quick_view', 'ecommerce');
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={14}
        color={i < Math.floor(rating) ? "#F59E0B" : "#E5E7EB"}
        className={i < Math.floor(rating) ? "fill-current" : ""}
      />
    ));
  };

  const getDiscountPercentage = () => {
    if (product?.originalPrice && product?.price < product?.originalPrice) {
      return Math.round(((product?.originalPrice - product?.price) / product?.originalPrice) * 100);
    }
    return 0;
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Product Image */}
      <div className="relative overflow-hidden rounded-t-lg bg-muted">
        <Image
          src={product?.image}
          alt={product?.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col space-y-1">
          {product?.isNew && (
            <span className="bg-accent text-white text-xs font-medium px-2 py-1 rounded">
              New
            </span>
          )}
          {getDiscountPercentage() > 0 && (
            <span className="bg-error text-white text-xs font-medium px-2 py-1 rounded">
              -{getDiscountPercentage()}%
            </span>
          )}
          {product?.stock <= 5 && product?.stock > 0 && (
            <span className="bg-warning text-white text-xs font-medium px-2 py-1 rounded">
              Low Stock
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isInWishlist 
              ? 'bg-error text-white' :'bg-white/80 text-text-secondary hover:bg-white hover:text-error'
          }`}
        >
          <Icon 
            name="Heart" 
            size={16} 
            className={isInWishlist ? "fill-current" : ""} 
          />
        </button>

        {/* Quick Actions */}
        {isHovered && (
          <div className="absolute inset-x-2 bottom-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              className="flex-1 bg-white/90 backdrop-blur-sm"
              onClick={handleQuickView}
            >
              Quick View
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="BarChart3"
              className="bg-white/90 backdrop-blur-sm"
            />
          </div>
        )}
      </div>
      {/* Product Info */}
      <div className="p-4">
        {/* Category & Brand */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-text-secondary font-medium">
            {product?.category}
          </span>
          {product?.brand && (
            <span className="text-xs text-text-secondary">
              {product?.brand}
            </span>
          )}
        </div>

        {/* Product Name */}
        <h3 className="font-medium text-text-primary mb-2 line-clamp-2 leading-tight">
          {product?.name}
        </h3>

        {/* Rating & Reviews */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center space-x-1">
            {renderStars(product?.rating)}
          </div>
          <span className="text-sm text-text-secondary">
            ({product?.reviewCount})
          </span>
        </div>

        {/* Variants */}
        {product?.variants && product?.variants?.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {product?.variants?.map((variant) => (
                <button
                  key={variant?.id}
                  onClick={() => setSelectedVariant(variant)}
                  className={`text-xs px-2 py-1 rounded border transition-colors duration-200 ${
                    selectedVariant?.id === variant?.id
                      ? 'border-primary bg-primary text-white' :'border-border text-text-secondary hover:border-primary'
                  }`}
                >
                  {variant?.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Price */}
        <div className="flex items-center space-x-2 mb-3">
          <span className="text-lg font-bold text-text-primary">
            ${selectedVariant?.price || product?.price}
          </span>
          {product?.originalPrice && product?.originalPrice > product?.price && (
            <span className="text-sm text-text-secondary line-through">
              ${product?.originalPrice}
            </span>
          )}
        </div>

        {/* Stock Status */}
        <div className="mb-3">
          {product?.stock > 0 ? (
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={14} color="var(--color-success)" />
              <span className="text-sm text-success">
                {product?.stock > 10 ? 'In Stock' : `Only ${product?.stock} left`}
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Icon name="X" size={14} color="var(--color-error)" />
              <span className="text-sm text-error">Out of Stock</span>
            </div>
          )}
        </div>

        {/* Quantity & Add to Cart */}
        <div className="flex items-center space-x-2">
          <div className="flex items-center border border-border rounded">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary"
              disabled={quantity <= 1}
            >
              <Icon name="Minus" size={14} />
            </button>
            <span className="w-8 text-center text-sm font-medium">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary"
              disabled={quantity >= product?.stock}
            >
              <Icon name="Plus" size={14} />
            </button>
          </div>
          
          <button
            onClick={handleAddToCart}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;